import React, {useEffect, useState, useMemo } from 'react'
import "../App.css"
import { UserProvider } from '../contexts/UserContext';
import { CssEditorProvider } from '../contexts/CssEditorContext';
import IsUser from '../components/IsUser';
import { getCss, getConfig } from '../services/userServices';
import { useParams } from 'react-router-dom';

export default function UserMiddleware() {
  const [loadedCss, setLoadedCss] = useState(null);
  const [spinWheelId, setSpinWheelId] = useState(null);
  const params = useParams();

  useEffect(() => {
    const Control = async () => {
      // Resolve user id from URL
      let urlId = null;
      try {
        urlId = params?.id || window.location.pathname.split('/').filter(Boolean)[0] || null;
      } catch {}

      // Resolve spinWheelId via getConfig
      let resolvedSpinId = null;
      try {
        if (urlId) {
          const cfg = await getConfig(urlId);
          
          resolvedSpinId = cfg?.spinWheelId || cfg?.data?.spinWheelId || cfg?.spinwhellId || null;
        }
      } catch {}

      if (!resolvedSpinId) {
        // fallback from storage
        resolvedSpinId = localStorage.getItem('spinWheelId') || null;
      }

      if (resolvedSpinId) {
        setSpinWheelId(resolvedSpinId);
        localStorage.setItem('spinWheelId', String(resolvedSpinId));
      }

      const cssResponse = await getCss(resolvedSpinId);
        if (cssResponse && typeof cssResponse === 'object' && Object.keys(cssResponse).length > 0) {
            const data = (cssResponse.status === true && cssResponse.data)
              ? cssResponse.data
              : (cssResponse.data && typeof cssResponse.data === 'object')
                ? cssResponse.data
                : cssResponse;

            // Map flat API fields to CSS variables used in the app
            const mapApiToCssVars = (d) => {
              const vars = {};

              // Hero
              if (d.titleColor) vars['--hero-title'] = d.titleColor;
              if (d.subtitleColor) vars['--hero-subtitle'] = d.subtitleColor;

              // Background gradients
              const c1 = d.color1 || 'transparent';
              const c2 = d.color2 || 'transparent';
              const pageGradient = (c1 === 'transparent' && c2 === 'transparent')
                ? 'transparent'
                : `linear-gradient(135deg, ${c1} 0%, ${c2} 50%, ${c1} 100%)`;
              const heroGradient = (c1 === 'transparent' && c2 === 'transparent')
                ? 'transparent'
                : `linear-gradient(135deg, ${c2} 0%, ${c1} 50%, ${c2} 100%)`;
              vars['--page-bg'] = pageGradient;
              vars['--hero-bg'] = heroGradient;

              // Form
              if (d.inputBackground) vars['--form-input-bg'] = d.inputBackground;
              if (d.inputBorderColor) vars['--form-input-border'] = d.inputBorderColor;
              if (d.inputTextColor) vars['--form-input-text'] = d.inputTextColor;
              if (d.buttonBackground) vars['--form-button-bg'] = d.buttonBackground;
              if (d.buttonTextColor) vars['--form-button-text'] = d.buttonTextColor;
              if (d.buttonHoverColor) vars['--form-button-hover'] = d.buttonHoverColor;
              if (d.buttonBorderColor) vars['--form-button-border'] = d.buttonBorderColor;
              if (d.secondaryButtonBackground) vars['--form-secondary-button-bg'] = d.secondaryButtonBackground;
              if (d.secondaryButtonTextColor) vars['--form-secondary-button-text'] = d.secondaryButtonTextColor;
              if (d.secondaryButtonBorderColor) vars['--form-secondary-button-border'] = d.secondaryButtonBorderColor;

              // How To
              if (d.howToTitleColor) vars['--how-to-title'] = d.howToTitleColor;
              if (d.howToIconColor) vars['--how-to-icon'] = d.howToIconColor;
              if (d.howToTextColor) vars['--how-to-text'] = d.howToTextColor;
              if (d.howToBadgeColor) vars['--how-to-badge'] = d.howToBadgeColor;

              // Countdown
              if (d.countdownTitleColor) vars['--countdown-title'] = d.countdownTitleColor;

              return vars;
            };

            const mappedVars = mapApiToCssVars(data);
            // Apply immediately so UI updates even before provider effect
            try {
              for (const [k, v] of Object.entries(mappedVars)) {
                document.documentElement.style.setProperty(k, v);
              }
              // Also reflect page background instantly
              if (mappedVars['--page-bg']) {
                if (!(document.body.dataset && document.body.dataset.hasBgImage === 'true')) {
                  document.body.style.background = mappedVars['--page-bg'];
                }
              }

              // If bg-image.webp exists, use it as full-page background
              const tryApplyFullBackground = () => {
                const candidates = [
                  '/bg-image.webp',
                  `${window.location.origin}/bg-image.webp`,
                  'bg-image.webp'
                ];
                let applied = false;
                candidates.forEach((u) => {
                  if (applied) return;
                  const img = new Image();
                  img.onload = () => {
                    if (applied) return;
                    applied = true;
                    // prevent overrides from gradient logic
                    document.body.dataset.hasBgImage = 'true';
                    const htmlEl = document.documentElement;
                    htmlEl.style.minHeight = '100vh';
                    htmlEl.style.background = 'transparent';
                    document.body.style.minHeight = '100vh';
                    document.body.style.background = `url(${u}) center center / cover no-repeat fixed`;
                    // ensure individual props too
                    document.body.style.backgroundImage = `url(${u})`;
                    document.body.style.backgroundSize = 'cover';
                    document.body.style.backgroundRepeat = 'no-repeat';
                    document.body.style.backgroundPosition = 'center center';
                    document.body.style.backgroundAttachment = 'fixed';
                    document.documentElement.style.minHeight = '100%';
                    const mainContainer = document.getElementById('main-container');
                    if (mainContainer) mainContainer.style.background = 'transparent';
                    const hero = document.querySelector('.hero-section');
                    if (hero) hero.style.background = 'transparent';
                    const clearSelectors = ['#root', '.App', '.general-container'];
                    clearSelectors.forEach(sel => {
                      document.querySelectorAll(sel).forEach(el => {
                        if (el instanceof HTMLElement) {
                          el.style.background = 'transparent';
                        }
                      })
                    })
                    
                  };
                  img.onerror = () => {}
                  img.src = u;
                });
              };

              tryApplyFullBackground();
            } catch {}

            setLoadedCss(mappedVars);
        } else {
            
            // Still attempt to apply background image if present
            (function(){
              try {
                const candidates = ['/bg-image.webp', `${window.location.origin}/bg-image.webp`, 'bg-image.webp']
                let applied = false
                candidates.forEach((u)=>{
                  if (applied) return
                  const img = new Image()
                  img.onload = ()=>{
                    if (applied) return
                    applied = true
                    document.body.dataset.hasBgImage = 'true'
                    document.documentElement.style.minHeight = '100vh'
                    document.body.style.minHeight = '100vh'
                    document.body.style.background = `url(${u}) center center / cover no-repeat fixed`
                  }
                  img.src = u
                })
              } catch {}
            })()
        }
            
    }
    Control();
  },[]);

  // IsUser component'ini sadece bir kez render et
  const isUserComponent = useMemo(() => <IsUser />, []);

  return (
    <CssEditorProvider initialCss={loadedCss}>
      <UserProvider>
        <div className="App">
          <div className='general-container'>
            {isUserComponent}
          </div>
        </div>
      </UserProvider>
    </CssEditorProvider>
  )
}
